import { get, filter, includes } from "lodash";
import { isDemand, isSupply } from "src/util/directory-customerConfig";

/** Shared predicate: type config is selectable (has type, not disabled, not current). */
export const isSelectableGroupType = (t, currentTeamType) =>
  t && t.type && !t.disabled && t.id !== currentTeamType;

/**
 * Allowed type IDs for changing a team's type: same category (demand/supply/other),
 * allowed by parent childTypes, and valid for existing child teams.
 */
export const getAllowedTypeIds = (
  team,
  groupTypes,
  parentGroup,
  childTeams
) => {
  if (!team?.type || !groupTypes) {
    return [];
  }
  const isCurrentDemand = isDemand(groupTypes, { type: team.type });
  const isCurrentSupply = isSupply(groupTypes, { type: team.type });

  const parentChildTypes = parentGroup
    ? get(groupTypes, [parentGroup.type, "childTypes"], [])
    : null;

  const typesList = filter(Object.values(groupTypes), (t) =>
    isSelectableGroupType(t, team.type)
  );

  return filter(typesList, (typeConfig) => {
    if (isCurrentDemand && !typeConfig.isDemand) {
      return false;
    }
    if (isCurrentSupply && !typeConfig.isSupply) {
      return false;
    }
    if (!isCurrentDemand && !isCurrentSupply) {
      if (typeConfig.isDemand || typeConfig.isSupply) {
        return false;
      }
    }
    if (parentChildTypes?.length > 0) {
      if (!includes(parentChildTypes, typeConfig.id)) {
        return false;
      }
    }
    const childTypes = get(typeConfig, "childTypes", null) || [];
    if (childTypes.length > 0) {
      const allChildrenAllowed = childTeams.every((child) =>
        includes(childTypes, child.type)
      );
      if (!allChildrenAllowed) {
        return false;
      }
    }
    return true;
  }).map((t) => t.id);
};

/**
 * Group allowed types into demand / supply / other for Select options.
 */
export const getGroupedTypeOptions = (groupTypes, allowedTypeIds) => {
  const demand = [];
  const supply = [];
  const other = [];
  const allowedSet = new Set(allowedTypeIds);
  Object.values(groupTypes || {}).forEach((t) => {
    if (!t || t.disabled || !allowedSet.has(t.id)) {
      return;
    }
    const opt = { label: t.name || t.id, value: t.id };
    if (t.isDemand) {
      demand.push(opt);
    } else if (t.isSupply) {
      supply.push(opt);
    } else {
      other.push(opt);
    }
  });
  const groups = [];
  if (demand.length) {
    groups.push({ label: "Demand Types", options: demand });
  }
  if (supply.length) {
    groups.push({ label: "Supply Types", options: supply });
  }
  if (other.length) {
    groups.push({ label: "Other Types", options: other });
  }
  return groups;
};
